import { useEffect, useRef } from 'react';
import { gsap, prefersReducedMotion } from './motion';

/** Fixed background: grid, drifting orbs and a cursor-following glow. Also drives the card spotlight (`--mx/--my`). */
export function BgFx() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = ref.current;
    if (!host || prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      gsap.to('.orb-1', { x: 60, y: 40, duration: 14, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      gsap.to('.orb-2', { x: -50, y: 60, duration: 17, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    }, host);

    const glow = host.querySelector<HTMLElement>('.cursor-glow')!;
    const gx = gsap.quickTo(glow, 'x', { duration: 0.8, ease: 'power3' });
    const gy = gsap.quickTo(glow, 'y', { duration: 0.8, ease: 'power3' });
    const onMove = (e: PointerEvent) => {
      glow.style.opacity = '.9';
      gx(e.clientX);
      gy(e.clientY);
      const card = (e.target as Element | null)?.closest?.('.lp-card') as HTMLElement | null;
      if (card) {
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${e.clientX - r.left}px`);
        card.style.setProperty('--my', `${e.clientY - r.top}px`);
      }
    };
    const onLeave = () => (glow.style.opacity = '0');
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', onLeave);
    return () => {
      ctx.revert();
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return (
    <div className="bg-fx" aria-hidden="true" ref={ref}>
      <div className="bg-grid" />
      <div className="orb orb-1" />
      <div className="orb orb-2" />
      <div className="cursor-glow" />
    </div>
  );
}

/** Shared SVG defs (gradients / filters) referenced by the hero map, dashboard map and sparkline. */
export function SvgDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#a3e635" stopOpacity=".35" />
          <stop offset="1" stopColor="#a3e635" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="routeGrad" x1="0" x2="1">
          <stop offset="0" stopColor="#34d399" />
          <stop offset="1" stopColor="#a3e635" />
        </linearGradient>
        <filter id="glowF" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <pattern id="mgrid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M40 0H0V40" fill="none" stroke="currentColor" strokeOpacity=".08" />
        </pattern>
        <radialGradient id="fence" cx="50%" cy="50%">
          <stop offset="0" stopColor="#a3e635" stopOpacity=".22" />
          <stop offset="1" stopColor="#a3e635" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
}
